/**
 * Climbing difficulty conversion utility.
 * Features a universal numeric scale for easy sorting and comparison.
 * Supports V-scale (Hueco) and French Fontainebleau scale for modeling.
 */

export interface DifficultyGrade {
  universal: number; // 0-indexed universal numeric scale, now more granular
  v: string; // V-scale (e.g., "V3")
  french: string; // French Fontainebleau (e.g., "6A", "6A+")
  band: string; // General difficulty band (e.g., "Beginner", "Intermediate")
  display: string; // Human-readable display format, unique for each entry
}

// **FIXED & EXPANDED MAPPING TABLE**
// This table is now more granular to include all French sub-grades.
// Note that some V-grades are repeated to cover the full range of an equivalent French grade.
// The order is intentional: when searching for a V-grade, the first match is the most common association.
const difficultyMapping: DifficultyGrade[] = [
  { universal: 0, v: "VB", french: "3", band: "Beginner", display: "VB / 3" },
  { universal: 1, v: "V0", french: "4", band: "Beginner", display: "V0 / 4" },
  { universal: 2, v: "V1", french: "5", band: "Beginner", display: "V1 / 5" },
  { universal: 3, v: "V2", french: "5+", band: "Beginner", display: "V2 / 5+" },
  {
    universal: 4,
    v: "V3",
    french: "6A",
    band: "Intermediate",
    display: "V3 / 6A",
  },
  {
    universal: 5,
    v: "V3",
    french: "6A+",
    band: "Intermediate",
    display: "V3 / 6A+",
  },
  {
    universal: 6,
    v: "V4",
    french: "6B",
    band: "Intermediate",
    display: "V4 / 6B",
  },
  {
    universal: 7,
    v: "V4",
    french: "6B+",
    band: "Intermediate",
    display: "V4 / 6B+",
  },
  { universal: 8, v: "V5", french: "6C", band: "Advanced", display: "V5 / 6C" },
  {
    universal: 9,
    v: "V6",
    french: "6C+",
    band: "Advanced",
    display: "V6 / 6C+",
  }, // V5/V6 overlap on 6C/6C+ is common
  {
    universal: 10,
    v: "V6",
    french: "7A",
    band: "Advanced",
    display: "V6 / 7A",
  },
  {
    universal: 11,
    v: "V7",
    french: "7A+",
    band: "Advanced",
    display: "V7 / 7A+",
  },
  { universal: 12, v: "V8", french: "7B", band: "Expert", display: "V8 / 7B" },
  {
    universal: 13,
    v: "V8",
    french: "7B+",
    band: "Expert",
    display: "V8 / 7B+",
  },
  { universal: 14, v: "V9", french: "7C", band: "Expert", display: "V9 / 7C" },
  {
    universal: 15,
    v: "V10",
    french: "7C+",
    band: "Expert",
    display: "V10 / 7C+",
  },
  { universal: 16, v: "V11", french: "8A", band: "Elite", display: "V11 / 8A" },
  {
    universal: 17,
    v: "V12",
    french: "8A+",
    band: "Elite",
    display: "V12 / 8A+",
  },
  { universal: 18, v: "V13", french: "8B", band: "Elite", display: "V13 / 8B" },
  {
    universal: 19,
    v: "V14",
    french: "8B+",
    band: "Elite",
    display: "V14 / 8B+",
  },
  { universal: 20, v: "V15", french: "8C", band: "Pro", display: "V15 / 8C" },
  { universal: 21, v: "V16", french: "8C+", band: "Pro", display: "V16 / 8C+" },
  { universal: 22, v: "V17", french: "9A", band: "Pro", display: "V17 / 9A" },
  {
    universal: 23,
    v: "V18",
    french: "9A+",
    band: "Legendary",
    display: "V18 / 9A+",
  },
  {
    universal: 24,
    v: "V19",
    french: "9B",
    band: "Legendary",
    display: "V19 / 9B",
  },
  {
    universal: 25,
    v: "V20",
    french: "9B+",
    band: "Legendary",
    display: "V20 / 9B+",
  },
  {
    universal: 26,
    v: "V21",
    french: "9C",
    band: "Legendary",
    display: "V21 / 9C",
  },
];

// Helper to normalize grade strings for reliable matching.
const normalize = (str: string) =>
  str.trim().toUpperCase().replace("/", "").replace(" ", "");

/**
 * Convert from V-scale to other formats.
 * Returns the first, most common match for a given V-grade.
 */
export function convertFromVScale(vGrade: string): DifficultyGrade | null {
  const normalized = normalize(vGrade);
  // Find the most common association (e.g., V4 -> 6B+) by searching the reversed array first.
  // This ensures V4 matches "V4 / 6B+" instead of "V4 / 6B".
  return (
    [...difficultyMapping]
      .reverse()
      .find((grade) => normalize(grade.v) === normalized) || null
  );
}

/**
 * Convert from French Fontainebleau to other formats.
 * This is now more precise as each French grade has a unique entry.
 */
export function convertFromFrench(frenchGrade: string): DifficultyGrade | null {
  const normalized = normalize(frenchGrade);
  return (
    difficultyMapping.find((grade) => normalize(grade.french) === normalized) ||
    null
  );
}

/**
 * Get all available grades for display.
 */
export function getAllGrades(): DifficultyGrade[] {
  return difficultyMapping;
}

/**
 * Parse any difficulty input and return the corresponding grade object.
 * It intelligently checks V-scale, French, and combined display formats.
 */
export function parseDifficulty(input: string): DifficultyGrade | null {
  if (!input || input.trim() === "") return null;

  const trimmed = input.trim();

  // Try to match display format first for perfect accuracy (e.g., "V4 / 6B")
  const displayMatch = difficultyMapping.find(
    (g) => normalize(g.display) === normalize(trimmed)
  );
  if (displayMatch) return displayMatch;

  // Try French scale (e.g., "6A", "7b+")
  let grade = convertFromFrench(trimmed);
  if (grade) return grade;

  // Try V-scale last (e.g., "V5")
  grade = convertFromVScale(trimmed);
  if (grade) return grade;

  return null;
}

/**
 * Validate if a difficulty string is valid.
 */
export function isValidDifficulty(input: string): boolean {
  return parseDifficulty(input) !== null;
}

/**
 * Get suggested difficulties based on partial input.
 */
export function getSuggestedDifficulties(
  input: string,
  limit = 8
): DifficultyGrade[] {
  if (!input || input.trim() === "") return difficultyMapping.slice(0, limit);

  const searchTerm = input.trim().toLowerCase();

  const getRelevance = (grade: DifficultyGrade, term: string): number => {
    const vMatch = grade.v.toLowerCase();
    const frenchMatch = grade.french.toLowerCase();
    if (vMatch === term || frenchMatch === term) return 3;
    if (vMatch.startsWith(term) || frenchMatch.startsWith(term)) return 2;
    if (grade.display.toLowerCase().includes(term)) return 1;
    return 0;
  };

  return difficultyMapping
    .map((grade) => ({ grade, relevance: getRelevance(grade, searchTerm) }))
    .filter((item) => item.relevance > 0)
    .sort(
      (a, b) =>
        b.relevance - a.relevance || a.grade.universal - b.grade.universal
    )
    .map((item) => item.grade)
    .slice(0, limit);
}

/**
 * Get the universal numeric difficulty level for sorting.
 */
export function getDifficultyLevel(grade: DifficultyGrade): number {
  return grade.universal;
}

/**
 * Convert from universal grade number to DifficultyGrade object.
 */
export function getGradeFromUniversal(
  universal: number
): DifficultyGrade | null {
  return (
    difficultyMapping.find((grade) => grade.universal === universal) || null
  );
}

/**
 * Get grade display string based on user preference.
 * @param universal - The universal grade number
 * @param preference - User's grade preference ('v-scale' or 'font')
 * @returns Formatted grade string according to preference
 */
export function getGradeDisplayByPreference(
  universal: number,
  preference: "v-scale" | "font" = "v-scale"
): string {
  const grade = getGradeFromUniversal(universal);
  if (!grade) return `Grade ${universal}`;

  switch (preference) {
    case "font":
      return grade.french;
    default:
      return grade.v;
  }
}

/**
 * Get full grade display with both scales based on user preference.
 * Shows preferred scale first, then secondary scale in parentheses.
 * @param universal - The universal grade number
 * @param preference - User's grade preference ('v-scale' or 'font')
 * @returns Formatted grade string with both scales
 */
export function getFullGradeDisplayByPreference(
  universal: number,
  preference: "v-scale" | "font" = "v-scale"
): string {
  const grade = getGradeFromUniversal(universal);
  if (!grade) return `Grade ${universal}`;

  switch (preference) {
    case "font":
      return `${grade.french} (${grade.v})`;
    default:
      return `${grade.v} (${grade.french})`;
  }
}

/**
 * Sort an array of items (e.g., routes) by difficulty.
 * Updated to work with numeric difficulty_rating.
 */
export function sortByDifficulty<T extends { difficulty_rating: number }>(
  items: T[]
): T[] {
  return [...items].sort((a, b) => {
    return a.difficulty_rating - b.difficulty_rating;
  });
}
